import React, { useRef, useEffect } from 'react';
import { Row, Form } from 'react-bootstrap';
import { useFormContext } from 'react-hook-form';
import FormTextAreaField from '../../formFields/FormTextAreaField';
import FormSelectField from '../../formFields/FormSelectField';
import FormDateTimeField from '../../formFields/FormDateTimeField';
import FormGeoLocationField from '../../formFields/FormGeoLocationField';
import { ResourceRequestFormFields } from '../utils/ResourceRequestTypes';
import { priorityOptions } from '../utils/ResourceRequestUtils';
import ResourceSelectFields from 'components/IAP/ResourceSelectFields';
import { useWorkAssignments } from 'components/IAP/hooks/workAssignmentHooks';
import FormTextField from 'components/IAP/formFields/FormTextField';
import { ActionAttachmentsSection } from 'components/IAP/actionTicket/ActionAttachmentsSection';
import { useActualIncidentId } from 'components/IAP/hooks/commonHooks';

interface ResourceRequestDetailsProps {
  resourceSites: Array<{ id: string; name: string }>;
  isCompleted: boolean;
  requestData?: any; // Existing request data if editing
}

const ResourceRequestDetails: React.FC<ResourceRequestDetailsProps> = ({
  resourceSites,
  isCompleted,
}) => {
  const { register, watch, setValue } =
    useFormContext<ResourceRequestFormFields>();
  const incidentId = useActualIncidentId();
  const attachments = watch('attachments') ?? [];
  const { data: workAssignments = [] } = useWorkAssignments();
  // Track initial kind value to determine if this was pre-filled (from requirement)
  const initialKindRef = useRef<string | null>(null);
  const currentKind = watch('kind');

  useEffect(() => {
    if (initialKindRef.current === null) {
      initialKindRef.current = currentKind || '';
    }
  }, [currentKind]);

  // Resource site options
  const siteOptions =
    resourceSites?.map((site) => ({
      value: site.id,
      label: site.name,
    })) || [];

  return (
    <>
      <Row>
        <div className="col-12 mb-3">
          <FormTextField
            title="Request Name"
            fieldName="name"
            rules={{
              required: { value: true, message: 'This field is required' },
              maxLength: {
                value: 300,
                message: 'This maximum length is 300 characters',
              },
            }}
            variant="half"
          />
        </div>
      </Row>

      <Row>
        {isCompleted ? (
          <>
            <div className="col-12 mt-2">
              <label className="form-label">Suggested Sources of Supply</label>
              <textarea
                className="form-control"
                {...register('supply_sources')}
                disabled={isCompleted}
              />
            </div>
          </>
        ) : (
          <FormTextAreaField
            fieldName="supply_sources"
            title="Suggested Sources of Supply"
            variant="full"
          />
        )}
      </Row>

      <Row>
        {isCompleted ? (
          <>
            <div className="col-12 mt-2">
              <label className="form-label">Acceptable Substitutes</label>
              <textarea
                className="form-control"
                {...register('substitutes')}
                disabled={isCompleted}
              />
            </div>
          </>
        ) : (
          <FormTextAreaField
            fieldName="substitutes"
            title="Acceptable Substitutes"
            variant="full"
          />
        )}
      </Row>
      <Row>
        <div className="col-12 mt-2">
          <FormSelectField
            disabled={isCompleted}
            fieldName={'work_assignment_ids'}
            title="Work Assignments"
            options={workAssignments.map((u) => ({
              label: u.work_assignment,
              value: u.work_assignment_id,
            }))}
            isMulti
            variant="half"
          />
        </div>
      </Row>
      {/* Resource Details - Always shown */}
      <hr />
      <h5>Resource Details</h5>
      <div className="border rounded p-3 mb-3">
        <ResourceSelectFields disabled={isCompleted} labels />
        <Row>
          <div className="col-6 mb-3">
            <label className="form-label">Unit</label>
            <input className="form-control" value={'Each'} readOnly disabled />
          </div>
        </Row>
        <Row>
          <div className="col-6 mb-3">
            <label className="form-label">Quantity</label>
            <input
              className="form-control"
              type="number"
              {...register('quantity', {
                required: {
                  value: true,
                  message: 'Quantity is required',
                },
                min: { value: 1, message: 'Quantity must be at least 1' },
                valueAsNumber: true,
              })}
              disabled={isCompleted}
            />
          </div>
        </Row>
        <Row>
          {isCompleted ? (
            <div className="col-12 mt-2">
              <label className="form-label">Description</label>
              <textarea
                className="form-control"
                {...register('description')}
                disabled={isCompleted}
              />
            </div>
          ) : (
            <FormTextAreaField
              fieldName="description"
              title="Description"
              variant="full"
            />
          )}
        </Row>
        <Row>
          <div className="col-12 mt-2">
            <Form.Check
              type="switch"
              id="custom-location"
              label="Use custom location instead of resource site"
              checked={watch('use_custom_location') || false}
              onChange={(e) =>
                setValue('use_custom_location', e.target.checked)
              }
              className="mb-2"
              disabled={isCompleted}
            />
          </div>
        </Row>
        <Row>
          <div className="col-6">
            {watch('use_custom_location') ? (
              <FormGeoLocationField
                name="requested_reporting_location_geolocation"
                label="Custom Reporting Location"
                disabled={isCompleted}
              />
            ) : isCompleted ? (
              <div className="mt-2">
                <label className="form-label">
                  Requested Reporting Location
                </label>
                <select
                  className="form-control"
                  {...register('requested_reporting_location_id')}
                  disabled={isCompleted}
                >
                  <option value="">Select a resource site...</option>
                  {siteOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <FormSelectField
                fieldName="requested_reporting_location_id"
                title="Requested Reporting Location"
                options={siteOptions}
                variant="full"
              />
            )}
          </div>
        </Row>
        <Row>
          {isCompleted ? (
            <div className="col-6 mt-2">
              <label className="form-label">Req. Reporting Date</label>
              <input
                className="form-control"
                type="datetime-local"
                {...register('requested_reporting_datetime')}
                disabled={isCompleted}
              />
            </div>
          ) : (
            <FormDateTimeField
              title="Req. Reporting Date"
              fieldName="requested_reporting_datetime"
              variant="half"
              type="datetime-local"
            />
          )}
        </Row>
        <Row>
          {isCompleted ? (
            <div className="col-6 mt-2">
              <label className="form-label">Priority</label>
              <select
                className="form-control"
                {...register('priority')}
                disabled={isCompleted}
              >
                {priorityOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <FormSelectField
              fieldName="priority"
              title="Priority"
              options={priorityOptions}
              variant="half"
            />
          )}
        </Row>
        <div className="dark tw:rounded-lg tw:bg-background tw:text-foreground">
          <ActionAttachmentsSection
            incidentId={incidentId}
            value={attachments}
            onChange={(next) =>
              setValue('attachments', next, { shouldDirty: true })
            }
          />
        </div>
      </div>
    </>
  );
};

export default ResourceRequestDetails;
